import React from 'react';
import moment from 'moment-timezone';
import DateInput from 'react/components/date_input';
import Button from 'react/components/button';
import Input from 'react/components/input';
import type { Contact } from 'react/types/contact';
import type {
  CancellationPolicy,
  CancellationReason,
  MembershipCancellation,
  MembershipWithCancellation,
} from 'react/types/membership_cancellation';

type CancellationStepOneProps = {
  contact: Contact;
  membership: MembershipWithCancellation;
  cancellationPolicy: Partial<CancellationPolicy>;
  currency: string;
  cancellationData: MembershipCancellation;
  reasons: CancellationReason[];
  errors: { cancellation?: { cancel_on?: string[] } };
  minDate: Date;
  maxDate: Date;
  onCancellationDataChange: (data: MembershipCancellation) => void;
  onManageOptions: () => void;
  onContinue: () => void;
  onCancel: () => void;
  isCancellationDateValid: boolean;
  isFirstStepReady: boolean;
};

const CancellationStepOne = (props: CancellationStepOneProps) => {
  const {
    contact,
    membership,
    cancellationPolicy,
    currency,
    cancellationData,
    reasons,
    errors,
    minDate,
    maxDate,
    onCancellationDataChange,
    onManageOptions,
    onContinue,
    onCancel,
    isCancellationDateValid,
    isFirstStepReady,
  } = props;
  const handleReasonChange = (reasonId: string): void => {
    onCancellationDataChange({
      ...cancellationData,
      membership_cancellation_reason_id: reasonId,
      reason: undefined,
    });
  };

  return (
    <main className="sub-content">
      <div>
        <h4>
          {contact.name} - {membership.label}
        </h4>

        <div className="suspension-info-box suspension-info-box--narrow">
            <p className="inline-icon-text">
              <strong className="inline-icon-text">
                <i className="icon-information ir-suspension-information-icon" />
                {' '}{membership.label} cancellation policy summary:
              </strong>
            </p>
            <ul>
              <li>
                Minimum notice period:{' '}
                <strong>
                  {cancellationPolicy.mobile_app_cancellation_notice_period_number}{' '}
                  {cancellationPolicy.notice_unit_label}
                </strong>
              </li>
              <li>
                Minimum membership length:{' '}
                <strong>
                  {cancellationPolicy.mobile_app_cancellation_minimum_term_number}{' '}
                  {cancellationPolicy.term_unit_label}
                </strong>
              </li>
              <li>
                Cancellation fee:{' '}
                {parseFloat(cancellationPolicy.fee_for_mobile_app_cancellations as string || '0') > 0 ? (
                  <strong>
                    {currency}{cancellationPolicy.fee_for_mobile_app_cancellations},{' '}
                    {cancellationPolicy.fee_strategy_text}
                  </strong>
                ) : (
                  <strong>Incurs no cancellation fee</strong>
                )}
              </li>
            </ul>
            <p>
              Fields are pre-filled with the minimum values required to meet the cancellation policy, but can be manually overridden.
            </p>
          </div>
      </div>

      <div className="row">
        <div className="col-sm-6">
          <form role="form">
            {!membership.membership_cancellation?.id && (
              <p>
                Current expiration date:{' '}
                {membership.expiration_date
                  ? moment(membership.expiration_date).format('MMM D YYYY')
                  : 'N/A'}
              </p>
            )}

            <div className="form-group">
              <label className="control-label margin-top-8">Cancel on</label>
              {!membership.membership_cancellation?.id ? (
                <>
                  <DateInput
                    value={cancellationData.cancel_on ? moment(cancellationData.cancel_on).format('YYYY-MM-DD') : ''}
                    onChange={(value) => onCancellationDataChange({ ...cancellationData, cancel_on: value })}
                    minDate={moment(minDate).format('YYYY-MM-DD')}
                    maxDate={moment(maxDate).format('YYYY-MM-DD')}
                    variant="v2"
                  />
                  {isCancellationDateValid && errors.cancellation?.cancel_on?.[0] && (
                    <div className="alert alert-danger is-sm">
                      {errors.cancellation.cancel_on[0]}
                    </div>
                  )}
                  {!isCancellationDateValid && (
                    <div className="alert alert-danger is-sm">
                      Should be before the membership's expiration date
                    </div>
                  )}
                </>
              ) : (
                <span className="margin-left-8">
                  {moment(membership.membership_cancellation.cancel_on).format('MMM D YYYY')}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="control-label">Reason for Cancelling (Optional)</label>
              <div className="flex">
                <div className="worx-select-control">
                  <select
                    className="form-control"
                    value={cancellationData.membership_cancellation_reason_id || ''}
                    onChange={(e) => handleReasonChange(e.target.value)}
                  >
                    <option value=""></option>
                    {reasons.map((reason) => (
                      <option key={reason.id} value={reason.id}>
                        {reason.name}
                      </option>
                    ))}
                  </select>
                </div>
                <Button variant="link" className="nowrap margin-left" onClick={onManageOptions}>
                  Manage options
                </Button>
              </div>
            </div>

            {cancellationData.membership_cancellation_reason_id === 'other' && (
              <div className="form-group">
                <label className="control-label">Please specify</label>
                <Input
                  value={cancellationData.reason || ''}
                  onChange={(value) =>
                    onCancellationDataChange({ ...cancellationData, reason: String(value) })
                  }
                />
              </div>
            )}
          </form>
        </div>
      </div>

      <footer className="worx-combo-box is-filled is-padded">
        <Button variant="success" onClick={onContinue} disabled={!isFirstStepReady}>
          Continue
        </Button>
        {' '}
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
      </footer>
    </main>
  );
};

export default CancellationStepOne;
